<template>
    <layout>
        <template v-slot:header>
            <AppHead :title="`Edit Product ${form.name}`" />
            <h2>Edit Product {{ form.name }}</h2>
            <BreadCrumbs activeItem="Edit Product" :crumbs="{ home: 'Dashboard', products: 'Products' }" />
            <flash-messages />
        </template>

        <template v-slot:default>
            <trashed-message v-if="product.deleted_at" class="mb-6" @restore="restore">
                This product has been deleted.
            </trashed-message>
            <div class="row">
                <div class="mb-4 d-flex flex-column flex-lg-row col-12 col-xl-6">
                    <jet-button class="mr-2 mb-2 mb-lg-0 w-100" icon="fas fa-images" @click="manageImages()">Manage
                        Images</jet-button>
                    <jet-button class="mr-2 mb-2 mb-lg-0 w-100" @click="manageBundle()">Manage Bundle</jet-button>
                    <jet-button class="mr-2" :goesTo="route('admin.products.variation', product)">Manage
                        Variation</jet-button>
                    <jet-button class="mr-2 mb-2 mb-lg-0 w-100" @click="activateAll()">Make Active for MTDs</jet-button>
                    <jet-button class="w-100 mb-2 mb-lg-0" type="mr-2"
                        :goesTo="this.route('products.copy', this.product.id)">Duplicate Product</jet-button>
                </div>
            </div>

            <form class="row" @submit.prevent="update">
                <div class="col-12 col-xl-6">
                    <card headerStyle="dark" icon="fas fa-tag" title="Product Info" class="mb-3">
                        <div class="row">
                            <div class="col-12">
                                <jet-label for="name" value="Name" />
                                <jet-input id="name" type="text" class="form-control" v-model="form.name"
                                    autocomplete="name" />
                                <jet-input-error :message="form.errors.name" class="mt-2" />
                            </div>
                            <div class="col-12 mt-2">
                                <div class="form-check">
                                    <input type="checkbox" id="active" class="form-check-input" v-model="form.active" />
                                    <jet-label for="active" value="Active" />
                                    <jet-input-error :message="form.errors.active" class="mt-2" />
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="form-group col-sm-4">
                                <jet-label for="sku" value="SKU" />
                                <jet-input id="sku" type="text" class="form-control" v-model="form.sku" />
                                <jet-input-error :message="form.errors.sku" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-4">
                                <jet-label for="manufacturer_reference" value="Manufacturer Reference" />
                                <jet-input id="manufacturer_reference" type="text" class="form-control"
                                    v-model="form.manufacturer_reference" />
                                <jet-input-error :message="form.errors.manufacturer_reference" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-4">
                                <jet-label for="upc" value="UPC" />
                                <jet-input id="upc" type="text" class="form-control" v-model="form.upc" />
                                <jet-input-error :message="form.errors.upc" class="mt-2" />
                            </div>
                        </div>
                    </card>

                    <card headerStyle="dark" title="Belongs To" class="mb-3">
                        <div class="row">
                            <div class="form-group col-12 col-lg-6">
                                <jet-label for="manufacturer" value="Manufacturer" />
                                <select v-model="form.manufacturer_id" class="form-control" id="manufacturer">
                                    <option v-for="man in manufacturers" :key="man.id" :value="man.id">
                                        {{ man.name }}
                                    </option>
                                    <option value="-1">New Manufacturer</option>
                                </select>

                                <div v-if="form.manufacturer_id == -1">
                                    <jet-label for="new_manufacturer" value="New Manufacturer" />
                                    <jet-input id="new_manufacturer" type="text" class="form-control"
                                        v-model="form.new_manufacturer_name" autocomplete="sku" />
                                </div>

                                <jet-input-error :message="form.errors.manufacturer" class="mt-2" />
                            </div>

                            <div class="form-group col-12 col-lg-6">
                                <jet-label for="categories" value="Categories" />
                                <Multiselect v-model="form.categories" mode="tags" :options="categories" :searchable="true"
                                    id="category_select" />
                                <jet-input-error :message="form.errors.categories" class="mt-2" />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-sm-4">
                                <div class="form-check">
                                    <input type="checkbox" id="wholesale_only" class="form-check-input"
                                        v-model="form.wholesale_only" />
                                    <jet-label for="wholesale_only" value="Wholesale Only" />
                                    <jet-input-error :message="form.errors.wholesale_only" class="mt-2" />

                                    <div class="alert alert-danger" v-if="!product.wholesale_only && form.wholesale_only">
                                        Warning: if turned on after has been off, MTD will lose price/inventory settings and
                                        carts will have this item removed.</div>
                                </div>
                            </div>
                        </div>
                    </card>

                    <card headerStyle="dark" title="Products in this Set" class="mb-3" v-if="ProductSetParts">
                        <div class="table-responsive">
                            <ProductSetDetails :ProductId="product" :ProductSetParts="ProductSetParts" :EditMode="true">
                            </ProductSetDetails>
                        </div>
                    </card>

                    <card title="Manufacturer Warranty Information" class="mb-3">
                        <div v-if="manufacturerWarranty">
                            <div class="row">
                                <div class="col-sm-6 form-group">
                                    <jet-label value="Warranty Period Days" class="text-secondary" />
                                    <jet-input disabled min="0" type="number" class="form-control"
                                        :value="manufacturerWarranty?.warranty_period_days" />
                                </div>
                                <div class="col-sm-6">
                                    <a :href="route('manufacturers.edit', this.form.manufacturer_id)" target="blank">Edit
                                        Manufacturer Warranty</a>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-sm-8 form-group">
                                    <jet-label for="warranty_link" value="Warranty Link" class="text-secondary" />
                                    <jet-input disabled id="warranty_link" type="text" class="form-control"
                                        :value="manufacturerWarranty?.warranty_link" />
                                </div>
                            </div>
                            <div class="row mx-1">
                                <div class="col-sm form-check">
                                    <jet-checkbox disabled :checked="manufacturerWarranty?.serial_number_required"
                                        class="form-check-input" />
                                    <jet-label value="SerialNumber Required" class="text-secondary" />
                                </div>
                                <div class="col-sm form-check">
                                    <jet-checkbox disabled :checked="manufacturerWarranty?.picture_required"
                                        class="form-check-input" />
                                    <jet-label value="Picture Required" class="text-secondary" />
                                </div>
                                <div class="col-sm form-check">
                                    <jet-checkbox disabled :checked="manufacturerWarranty?.return_required"
                                        class="form-check-input" />
                                    <jet-label value="Return Required" class="text-secondary" />
                                </div>
                                <div class="col-sm form-check">
                                    <jet-checkbox disabled :checked="manufacturerWarranty?.manufacturer_direct"
                                        class="form-check-input" />
                                    <jet-label value="Manufacturer Direct" class="text-secondary" />
                                </div>
                            </div>
                        </div>
                        <div v-else class="row">
                            <div class="col-sm-6">
                                <span>No Manufacturer Warranty Set</span>
                            </div>
                            <div class="col-sm-6">
                                <a :href="route('manufacturers.edit', this.form.manufacturer_id)" target="blank">Edit
                                    Manufacturer Warranty</a>
                            </div>
                        </div>
                    </card>

                    <card headerStyle="dark" title="Product Warranty Information" class="mb-3">
                        <div class="row" v-if="!form.use_manufacturer_warranty">
                            <div class="col-lg-4 form-group">
                                <jet-label for="warranty_period_days" value="Warranty Period Days" />
                                <jet-input id="warranty_period_days" min="0" type="number" class="form-control"
                                    v-model="form.warranty_period_days" />
                                <jet-input-error :message="form.errors.warranty_period_days" class="mt-2" />
                            </div>
                        </div>
                        <div class="row" v-if="!form.use_manufacturer_warranty">
                            <div class="col-sm-8 form-group">
                                <jet-label for="warranty_link" value="Warranty Link" />
                                <jet-input id="warranty_link" type="text" class="form-control"
                                    v-model="form.warranty_link" />
                                <jet-input-error :message="form.errors.warranty_link" class="mt-2" />
                            </div>
                        </div>
                        <div class="row mx-1" v-if="!form.use_manufacturer_warranty">
                            <div class="col-sm form-check">
                                <jet-checkbox id="serial_number_required" v-model:checked="form.serial_number_required" />
                                <jet-label for="serial_number_required" value="SerialNumber Required" />
                                <jet-input-error :message="form.errors.serial_number_required" class="mt-2" />
                            </div>
                            <div class="col-sm form-check">
                                <jet-checkbox id="picture_required" v-model:checked="form.picture_required" />
                                <jet-label for="picture_required" value="Picture Required" />
                                <jet-input-error :message="form.errors.picture_required" class="mt-2" />
                            </div>
                            <div class="col-sm form-check">
                                <jet-checkbox id="return_required" v-model:checked="form.return_required" />
                                <jet-label for="return_required" value="Return Required" />
                                <jet-input-error :message="form.errors.return_required" class="mt-2" />
                            </div>
                            <div class="col-sm form-check">
                                <jet-checkbox id="manufacturer_direct" v-model:checked="form.manufacturer_direct" />
                                <jet-label for="manufacturer_direct" value="Manufacturer Direct" />
                                <jet-input-error :message="form.errors.manufacturer_direct" class="mt-2" />
                            </div>
                        </div>
                        <div class="row mx-1 mt-2">
                            <div class="col-sm-6 form-check">
                                <jet-checkbox id="use_manufacturer_warranty"
                                    v-model:checked="form.use_manufacturer_warranty" />
                                <jet-label for="use_manufacturer_warranty" value="Use Manufacturer Warranty" />
                                <jet-input-error :message="form.errors.use_manufacturer_warranty" class="mt-2" />
                            </div>
                            <div class="col-sm-6 form-group">
                                <a :href="route('manufacturers.edit', this.form.manufacturer_id)" target="blank">Edit
                                    Manufacturer Warranty</a>
                            </div>
                        </div>
                    </card>

                    <card headerStyle="dark" title="Descriptions">
                        <div class="row">
                            <div class="form-group col-12">
                                <jet-label for="short_description" value="Short Description" />
                                <jet-input id="short_description" class="form-control" v-model="form.short_description" />
                                <jet-input-error :message="form.errors.short_description" class="mt-2" />
                            </div>
                            <div class="form-group col-12">
                                <jet-label for="long_description" value="Long Description" />
                                <ckeditor id="long_description" :editor="editor" v-model="form.long_description"
                                    :config="editorConfig"></ckeditor>
                                <jet-input-error :message="form.errors.long_description" class="mt-2" />
                            </div>
                        </div>
                    </card>
                </div>

                <div class="col-12 col-xl-6">
                    <card headerStyle="dark" title="Inventory" class="mb-3">
                        <div class="row">
                            <div class="form-group col-12 col-lg-6">
                                <jet-label for="current_inventory" value="Current Inventory" />
                                <jet-input id="current_inventory" type="text" class="form-control"
                                    v-model="form.current_inventory" :disabled="isBundle" />
                                <jet-input-error :message="form.errors.current_inventory" class="mt-2" />
                                <div class="mt-2">
                                    <div class="font-weight-bold">Included Products Inventory:</div>
                                    <div v-for="child in product.included_products || []">
                                        <a :href="route('products.edit', child.id)" target="_blank">{{ child.sku }} ({{
                                            child.current_inventory }})</a>
                                    </div>
                                </div>
                                <div class="mt-2">
                                    <div class="form-check">
                                        <input type="checkbox" id="backorder_enabled_mtd" class="form-check-input"
                                            v-model="form.backorder_enabled_mtd" />
                                        <jet-label for="backorder_enabled_mtd" value="Backorder Enabled - MTD" />
                                        <jet-input-error :message="form.errors.backorder_enabled_mtd" class="mt-2" />
                                    </div>
                                    <div class="form-check">
                                        <input type="checkbox" id="backorder_enabled_tu" class="form-check-input"
                                            v-model="form.backorder_enabled_tu" />
                                        <jet-label for="backorder_enabled_tu" value="Backorder Enabled - Tool User" />
                                        <jet-input-error :message="form.errors.backorder_enabled_tu" class="mt-2" />
                                    </div>
                                </div>
                            </div>

                            <div class="form-group col-sm-4">
                                <jet-label for="backorder_eta" value="Backorder ETA" />
                                <jet-input id="backorder_eta" type="text" class="form-control"
                                    v-model="form.backorder_eta" /> days
                                <jet-input-error :message="form.errors.backorder_eta" class="mt-2" />
                            </div>
                        </div>

                        <div v-if="!isBundle && this.product.current_inventory > 0" class="pb-3">
                            <card v-if="returnToManufacturerExpanded" title="Return to Manufacturer" headerStyle="primary">
                                <jet-label for="returning_comment" value="Comments To Manufacturer" />
                                <textarea id="returning_comment" type="text" class="form-control" row
                                    v-model="returnForm.returning_comment" />
                                <div class="row align-items-end">
                                    <div class="col">
                                        <jet-label for="returning_quantity" value="Number Returning" />
                                        <jet-input id="returning_quantity" type="text" class="form-control"
                                            v-model="returnForm.returning_quantity" />
                                    </div>
                                    <div class="col">
                                        <jet-button @click="submitManufacturerReturn"
                                            :disabled="returnForm.processing">Remove From Inventory</jet-button>
                                        <JetDangerButton class="ml-3" :disabled="returnForm.processing"
                                            @click="() => returnToManufacturerExpanded = false">
                                            Cancel</JetDangerButton>
                                    </div>
                                </div>
                            </card>
                            <jet-button v-else @click="() => returnToManufacturerExpanded = true">Return to
                                Manufacturer</jet-button>
                        </div>

                        <div class="row inventory-location-table">
                            <div class="col-md-12">
                                <div class="p-inputgroup mb-2">
                                    <Dropdown size="small" dropdown v-model="inventoryLocationForm.row"
                                        :options="inventoryLocationRows" placeholder="Select Row" class="mr-2" />
                                    <Dropdown size="small" dropdown v-model="inventoryLocationForm.column"
                                        :options="inventoryLocationColumns" :disabled="!inventoryLocationForm.row"
                                        placeholder="Select Column" class="mr-2" />
                                    <Dropdown size="small" dropdown v-model="inventoryLocationForm.shelf"
                                        :options="inventoryLocationShelfs" :disabled="!inventoryLocationForm.column"
                                        placeholder="Select Shelf" class="mr-2" />
                                    <Dropdown size="small" dropdown v-model="inventoryLocationForm.slot"
                                        :options="inventoryLocationSlots" :disabled="!inventoryLocationForm.shelf"
                                        placeholder="Select Slot" />
                                </div>
                            </div>
                            <div class="col-md-12">
                                <div class="d-flex align-items-center justify-content-between">
                                    <div>
                                        <div v-if="selectedInventoryLocation?.id">Selected Inventory Location Id: {{
                                            selectedInventoryLocation?.id }} (Pick Order: {{
        selectedInventoryLocation?.pick_order }})</div>
                                    </div>
                                    <jet-button class="btn btn-primary mb-2" @click="addInventoryLocation()"
                                        :disabled="inventoryLocationForm.processing || !selectedInventoryLocation"
                                        :loading="inventoryLocationForm.processing">Set Inventory Location</jet-button>
                                </div>
                            </div>
                            <div class="col-md-12">
                                <InventoryLocation
                                    :inventoryLocations="product.inventory_location ? [product.inventory_location] : []"
                                    :handleDelete="deleteInventoryLocation"></InventoryLocation>
                            </div>
                        </div>
                    </card>

                    <card headerStyle="dark" title="Shipping" class="mb-3">
                        <div class="row">
                            <div class="form-group col-sm-6">
                                <jet-label for="ship_weight" value="Shipping - Weight" />
                                <jet-input id="ship_weight" type="text" class="form-control" v-model="form.ship_weight" />
                                <jet-input-error :message="form.errors.ship_weight" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-6">
                                <jet-label for="ship_height" value="Shipping - Height" />
                                <jet-input id="ship_height" type="text" class="form-control" v-model="form.ship_height" />
                                <jet-input-error :message="form.errors.ship_height" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-6">
                                <jet-label for="ship_width" value="Shipping - Width" />
                                <jet-input id="ship_width" type="text" class="form-control" v-model="form.ship_width" />
                                <jet-input-error :message="form.errors.ship_width" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-6">
                                <jet-label for="ship_length" value="Shipping - Length" />
                                <jet-input id="ship_length" type="text" class="form-control" v-model="form.ship_length" />
                                <jet-input-error :message="form.errors.ship_length" class="mt-2" />
                            </div>
                        </div>

                        <div class="row">
                            <div class="form-group col-sm-6">
                                <div class="form-check">
                                    <jet-checkbox id="ship_only_mtd" v-model="form.ship_only_mtd" />
                                    <jet-label for="ship_only_mtd" value="Shipping - Only to MTD" />
                                    <jet-input-error :message="form.errors.ship_only_mtd" class="mt-2" />
                                </div>
                            </div>
                            <div class="form-group col-sm-6">
                                <jet-label for="ship_flat_cost" value="Shipping - Flat Shipping Cost" />
                                <jet-input id="ship_flat_cost" type="text" class="form-control"
                                    v-model="form.ship_flat_cost" :disabled="isBundle" />
                                <jet-input-error :message="form.errors.ship_flat_cost" class="mt-2" />
                            </div>
                        </div>
                        <div class="row">
                            <div class="form-group col-12">
                                <jet-label for="source_location_id" value="Location" />
                                <select class="form-control" id="source_location_id" v-model="form.source_location_id">
                                    <option></option>
                                    <option v-for="address in warehouses" :key="address.id" :value="address.id">
                                        {{ address.company_name }} {{ address.address1 }} {{ address.city }}, {{
                                            address.state }} {{ address.postcode }}
                                    </option>
                                </select>
                                <jet-input-error :message="form.errors.source_location_id" class="mt-2" />
                            </div>
                        </div>

                    </card>

                    <card headerStyle="dark" title="Pricing" class="mb-3">
                        <div class="row" v-if="!isBundle">
                            <div class="form-group col-sm-6">
                                <jet-label for="price_msrp" value="Pricing - MSRP" />
                                <jet-input id="price_msrp" type="text" class="form-control" v-model="form.price_msrp"
                                    :disabled="isBundle" />
                                <jet-input-error :message="form.errors.price_msrp" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-6">
                                <jet-label for="wholesale_price_percent" value="Pricing - Wholesale Percent" />
                                <jet-input id="wholesale_price_percent" type="text" class="form-control"
                                    v-model="form.wholesale_price_percent" />
                                <jet-input-error :message="form.errors.wholesale_price_percent" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-6">
                                <jet-label for="wholesale_price_fixed" value="Pricing - Wholesale Fixed" />
                                <jet-input id="wholesale_price_fixed" type="text" class="form-control"
                                    v-model="form.wholesale_price_fixed" :disabled="isBundle" />
                                <jet-input-error :message="form.errors.wholesale_price_fixed" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-6">
                                <jet-label for="case_qty" value="Case Qty" />
                                <jet-input id="case_qty" type="text" class="form-control" v-model="form.case_qty" />
                                <jet-input-error :message="form.errors.case_qty" class="mt-2" />
                            </div>

                            <div class="form-group col-sm-6">
                                <jet-label for="price_mtn" value="MTN Price" />
                                <jet-input id="price_mtn" type="text" class="form-control" v-model="form.price_mtn" />
                                <jet-input-error :message="form.errors.price_mtn" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-6">
                                <div class="form-check">
                                    <input type="checkbox" id="exclude_mtd_discount" class="form-check-input"
                                        v-model="form.exclude_mtd_discount" />
                                    <jet-label for="exclude_mtd_discount" value="Exclude MTD Discount" />
                                    <jet-input-error :message="form.errors.exclude_mtd_discount" class="mt-2" />
                                </div>
                            </div>
                        </div>
                        <div class="row" v-if="isBundle">
                            <div class="form-group col-sm-6">
                                <jet-button class="mr-2 mb-2 mb-lg-0 w-100" @click="manageBundle()">Manage
                                    Bundle</jet-button>
                            </div>
                        </div>
                    </card>

                    <card headerStyle="dark" title="Video" class="mb-3">
                        <div class="row">
                            <div class="form-group col-sm-12">
                                <jet-label for="dealer_training_video_url" value="Dealer Training Video URL" />
                                <jet-input id="dealer_training_video_url" type="text" class="form-control"
                                    v-model="form.dealer_training_video_url" />
                                <jet-input-error :message="form.errors.dealer_training_video_url" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-12">
                                <jet-label for="dealer_training_video_description"
                                    value="Dealer Training Video Description" />
                                <jet-input id="dealer_training_video_description" type="text" class="form-control"
                                    v-model="form.dealer_training_video_description" />
                                <jet-input-error :message="form.errors.dealer_training_video_description" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-12">
                                <jet-label for="sales_video_url" value="Sales Video URL" />
                                <jet-input id="sales_video_url" type="text" class="form-control"
                                    v-model="form.sales_video_url" />
                                <jet-input-error :message="form.errors.sales_video_url" class="mt-2" />
                            </div>
                            <div class="form-group col-sm-12">
                                <jet-label for="sales_video_description" value="Sales Video Description" />
                                <jet-input id="sales_video_description" type="text" class="form-control"
                                    v-model="form.sales_video_description" />
                                <jet-input-error :message="form.errors.sales_video_description" class="mt-2" />
                            </div>
                        </div>
                    </card>
                </div>

                <div class="bottom-button mt-3 mx-3">
                    <jet-button class="px-5" btnStyle="success" :disable="form.processing" type="submit">Save</jet-button>
                </div>
            </form>
        </template>
    </layout>
</template>

<script>
import Layout from '@/Shared/Admin/Layout'
import JetFormSection from '@/Jetstream/FormSection'
import JetInput from '@/Jetstream/Input'
import JetCheckbox from '@/Jetstream/Checkbox'
import JetInputError from '@/Jetstream/InputError'
import JetLabel from '@/Jetstream/Label'
import JetActionMessage from '@/Jetstream/ActionMessage'
import JetDangerButton from '@/Jetstream/DangerButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import UploadAdapter from '@/UploadAdapter'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import Multiselect from '@vueform/multiselect'
import InventoryLocation from './InventoryLocation.vue'
import Dropdown from 'primevue/dropdown';
import ProductSetDetails from '../../../Components/Products/ProductSetDetails.vue'

export default {
    components: {
        JetActionMessage,
        JetFormSection,
        JetInput,
        JetCheckbox,
        JetInputError,
        JetLabel,
        JetDangerButton,
        InventoryLocation,
        Dropdown,
        Layout,
        TrashedMessage,
        Multiselect,
        BreadCrumbs,
        ckeditor: CKEditor.component,
        ProductSetDetails,
    },
    props: {
        product: Object,
        ProductSetParts: Array,
        inventoryLocations: Array,
        warranty: Object,
        price_mtn: Number,
        manufacturers: Array,
        warehouses: Array,
        categories: Array
    },
    remember: 'form',
    data() {
        return {
            editor: ClassicEditor,
            editorConfig: {
                extraPlugins: [this.uploader],
                product: this.product
            },
            form: this.$inertia.form({
                _method: 'put',
                active: this.product.active == 1,
                wholesale_only: this.product.wholesale_only == 1,
                name: this.product.name,
                sku: this.product.sku,
                manufacturer_reference: this.product.manufacturer_reference,
                upc: this.product.upc,
                manufacturer_id: this.product.manufacturer_id,
                short_description: this.product.short_description,
                long_description: this.product.long_description,
                ship_weight: this.product.ship_weight,
                ship_height: this.product.ship_height,
                ship_width: this.product.ship_width,
                ship_length: this.product.ship_length,
                ship_only_mtd: this.product.ship_only_mtd,
                ship_flat_cost: this.product.ship_flat_cost,
                price_msrp: this.product.price_msrp,
                price_mtn: this.price_mtn,
                wholesale_price_percent: this.product.wholesale_price_percent,
                wholesale_price_fixed: this.product.wholesale_price_fixed,
                source_location_id: this.product.source_location_id,
                current_inventory: this.product.current_inventory,
                backorder_enabled_mtd: this.product.backorder_enabled_mtd == 1,
                backorder_enabled_tu: this.product.backorder_enabled_tu == 1,
                backorder_eta: this.product.backorder_eta,
                address1: null,
                address2: null,
                city: null,
                state: null,
                postcode: null,
                categories: this.product.categories_selected,
                new_manufacturer_name: null,
                exclude_mtd_discount: this.product.exclude_mtd_discount == 1,
                case_qty: this.product.case_qty,
                dealer_training_video_url: this.product.dealer_training_video_url,
                dealer_training_video_description: this.product.dealer_training_video_description,
                sales_video_url: this.product.sales_video_url,
                sales_video_description: this.product.sales_video_description,
                use_manufacturer_warranty: this.warranty ? false : true,
                warranty_period_days: this.warranty?.warranty_period_days ?? 0,
                warranty_link: this.warranty?.warranty_link,
                serial_number_required: this.warranty?.serial_number_required,
                picture_required: this.warranty?.picture_required,
                return_required: this.warranty?.return_required,
            }),
            inventoryLocationForm: this.$inertia.form({
                inventory_location_id: null,
                shelf: null,
                row: null,
                column: null,
                slot: null,
            }),
            returnToManufacturerExpanded: false,
            returnForm: this.$inertia.form({
                returning_quantity: this.product.current_inventory,
                returning_comment: '',
            }),
        }
    },
    computed: {
        manufacturerWarranty() {
            return this.manufacturers.filter(m => m.id == this.form.manufacturer_id)[0]?.warranty
        },
        inventoryLocationRows() {
            const arr = this.inventoryLocations
                .map(v => v.row)
                .filter((v, i, a) => a.indexOf(v) === i)
            return arr
        },
        inventoryLocationColumns() {
            const arr = this.inventoryLocations
                .filter(v => v.row == this.inventoryLocationForm.row)
                .map(v => v.column)
                .filter((v, i, a) => a.indexOf(v) === i)
            return arr
        },
        inventoryLocationShelfs() {
            const arr = this.inventoryLocations
                .filter(v => v.row == this.inventoryLocationForm.row)
                .filter(v => v.column == this.inventoryLocationForm.column)
                .map(v => v.shelf)
                .filter((v, i, a) => a.indexOf(v) === i)
            return arr
        },
        inventoryLocationSlots() {
            const arr = this.inventoryLocations
                .filter(v => v.shelf == this.inventoryLocationForm.shelf)
                .filter(v => v.row == this.inventoryLocationForm.row)
                .filter(v => v.column == this.inventoryLocationForm.column)
                .map(v => v.slot)
                .filter((v, i, a) => a.indexOf(v) === i)
            return arr
        },
        selectedInventoryLocation() {
            const arr = this.inventoryLocations
                .filter(v => v.shelf == this.inventoryLocationForm.shelf)
                .filter(v => v.row == this.inventoryLocationForm.row)
                .filter(v => v.column == this.inventoryLocationForm.column)
                .filter(v => v.slot == this.inventoryLocationForm.slot)
            return arr[0]
        },
        isBundle() {
            return this.product?.included_products?.length > 0;
        }
    },
    watch: {
        'form.manufacturer_id': function (val) {
            const newWarranty = this.manufacturerWarranty
            if (this.form.use_manufacturer_warranty && newWarranty) {
                this.form.warranty_period_days = newWarranty.warranty_period_days ?? 0;
                this.form.warranty_link = newWarranty.warranty_link;
                this.form.serial_number_required = newWarranty.serial_number_required;
                this.form.picture_required = newWarranty.picture_required;
                this.form.return_required = newWarranty.return_required;
            }
        },
    },
    methods: {
        update() {
            this.form.post(this.route('products.update', this.product.id), {
                onSuccess: () => this.form.reset('password', 'photo'),
            })
        },
        destroy() {
            if (confirm('Are you sure you want to delete this product?')) {
                this.$inertia.delete(this.route('products.destroy', this.product.id))
            }
        },
        restore() {
            if (confirm('Are you sure you want to restore this product?')) {
                this.$inertia.put(this.route('products.restore', this.product.id))
            }
        },
        manageImages() {
            this.$inertia.visit(this.route('products.images', this.product.id));
        },
        manageBundle() {
            this.$inertia.visit(this.route('products.bundle', this.product.id));
        },
        copy() {
            this.$inertia.visit(this.route('products.copy', this.product.id));
        },
        activateAll() {
            this.$inertia.visit(this.route('products.activatemtd', this.product.id));
        },
        addInventoryLocation() {
            this.inventoryLocationForm.transform(data => {
                data.inventory_location_id = this.selectedInventoryLocation?.id
                return data
            })
                .post(this.route('admin.products.addInventoryLocation', this.product.id), {
                    preserveScroll: false,
                    onSuccess: () => {
                        this.inventoryLocationForm.reset()
                    }
                })
        },
        deleteInventoryLocation(event, inventoryLocation) {
            this.$inertia.form({})
                .delete(this.route('admin.products.deleteInventoryLocation', [this.product.id, inventoryLocation.id]), {
                    preserveScroll: false,
                    onSuccess: () => {
                    }
                })
        },

        uploader(editor) {
            editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                return new UploadAdapter(loader, editor.config._config.product);
            };
        },
        submitManufacturerReturn() {
            if (confirm('Are you sure you want to remove ' + this.returnForm.returning_quantity + ' from inventory?')) {
                this.returnForm.post(this.route('products.returntomanufacturer', this.product.id))
            }
        },
    }
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
